import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  messages,
  NEWSLETTER_STATUS_LABELS,
  NEWSLETTER_SUBSCRIBER_STATUS_LABELS,
  formatDateTime,
  type NewsletterStatus,
} from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { ResponsiveList, type Column } from '@/components/common/responsive-list';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** S75 メルマガ（配信・購読者管理） */
export const SettingsNewsletterPage = () => {
  const { filters, setFilter } = useUrlFilters({ tab: 'issues' });
  const tab = (filters.tab as string) === 'subscribers' ? 'subscribers' : 'issues';

  return (
    <>
      <PageHeader title={messages.settings.newsletter} />
      <div className="mb-4 inline-flex rounded-md border border-border p-0.5">
        {([['issues', '配信'], ['subscribers', '購読者']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter('tab', key)}
            className={cn(
              'rounded px-4 py-1.5 text-note font-medium transition-colors',
              tab === key ? 'bg-primary text-primary-foreground' : 'text-foreground-muted hover:bg-muted',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'issues' ? <IssuesTab /> : <SubscribersTab />}
    </>
  );
};

// ---------------------------------------------------------------- 配信（号）

type Issue = {
  id: string;
  subject: string;
  body: string;
  status: NewsletterStatus;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  sentAt: string | null;
  createdAt: string;
};
type IssueDraft = { id?: string; subject: string; body: string };

const ISSUE_VARIANT: Record<NewsletterStatus, 'solid' | 'default' | 'muted'> = {
  sent: 'muted',
  sending: 'solid',
  draft: 'default',
};

const IssuesTab = () => {
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState<IssueDraft | null>(null);
  const [sendTarget, setSendTarget] = useState<Issue | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Issue | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['newsletter', 'issues'],
    queryFn: () => api.get<{ data: Issue[] }>('/newsletter/issues'),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['newsletter', 'issues'] });

  const save = useMutation({
    mutationFn: (d: IssueDraft) =>
      d.id
        ? api.patch(`/newsletter/issues/${d.id}`, { subject: d.subject, body: d.body })
        : api.post('/newsletter/issues', { subject: d.subject, body: d.body }),
    onSuccess: () => { toast.success('保存しました'); invalidate(); setEdit(null); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const send = useMutation({
    mutationFn: (id: string) => api.post<{ data: { recipientCount: number } }>(`/newsletter/issues/${id}/send`, {}),
    onSuccess: (res) => { toast.success(`${res.data.recipientCount} 人へ配信を開始しました`); invalidate(); setSendTarget(null); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/newsletter/issues/${id}`),
    onSuccess: () => { toast.success('削除しました'); invalidate(); setRemoveTarget(null); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const progress = (r: Issue) =>
    r.status === 'draft' ? '—' : `宛先 ${r.recipientCount}・送信 ${r.sentCount}・失敗 ${r.failedCount}`;

  const columns: Column<Issue>[] = [
    { key: 'subject', header: '件名', render: (r) => <span className="break-all font-semibold">{r.subject}</span> },
    { key: 'status', header: '状態', width: '90px', render: (r) => <Badge variant={ISSUE_VARIANT[r.status]}>{NEWSLETTER_STATUS_LABELS[r.status]}</Badge> },
    { key: 'progress', header: '配信', render: (r) => <span className="text-note text-foreground-muted">{progress(r)}</span> },
    { key: 'createdAt', header: '作成日', width: '140px', render: (r) => <span className="tabular text-note">{formatDateTime(r.sentAt ?? r.createdAt)}</span> },
  ];

  const rowActions = (r: Issue) => (
    <span className="flex items-center gap-1">
      {r.status === 'draft' && (
        <>
          <Button variant="ghost" size="sm" onClick={() => setEdit({ id: r.id, subject: r.subject, body: r.body })}>編集</Button>
          <Button variant="ghost" size="sm" onClick={() => setSendTarget(r)}>配信</Button>
          <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(r)}>削除</Button>
        </>
      )}
      {r.status !== 'draft' && (
        <Button variant="ghost" size="sm" onClick={() => setEdit({ id: r.id, subject: r.subject, body: r.body })}>内容</Button>
      )}
    </span>
  );

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setEdit({ subject: '', body: '' })}>
          <Plus aria-hidden />
          メルマガを作成
        </Button>
      </div>

      <ResponsiveList
        columns={columns}
        rows={data?.data ?? []}
        isLoading={isLoading}
        rowActions={rowActions}
        empty={<EmptyState title="メルマガがありません" />}
        card={(r) => (
          <div className="space-y-1.5">
            <p className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 break-all text-body font-semibold">{r.subject}</span>
              <Badge variant={ISSUE_VARIANT[r.status]}>{NEWSLETTER_STATUS_LABELS[r.status]}</Badge>
            </p>
            <p className="text-note text-foreground-muted">{progress(r)}</p>
            <p className="tabular text-note text-foreground-subtle">{formatDateTime(r.sentAt ?? r.createdAt)}</p>
            {rowActions(r)}
          </div>
        )}
      />

      {/* 作成・編集・内容 */}
      <Dialog open={edit != null} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit?.id ? 'メルマガ' : 'メルマガを作成'}</DialogTitle>
          </DialogHeader>
          {edit && (
            <DialogBody className="space-y-4">
              <Field label="件名">
                {(props) => (
                  <Input {...props} value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} maxLength={200} />
                )}
              </Field>
              <Field label="本文" hint="URL は自動でリンクになります。末尾に配信停止リンクが自動で付きます。">
                {(props) => (
                  <Textarea
                    {...props}
                    value={edit.body}
                    onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                    rows={12}
                    placeholder={'いつもお世話になっております。\n\n（本文）'}
                  />
                )}
              </Field>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>閉じる</Button>
            <Button
              onClick={() => edit && save.mutate(edit)}
              disabled={save.isPending || !edit?.subject.trim() || !edit?.body.trim()}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={sendTarget != null}
        onOpenChange={(o) => { if (!o) setSendTarget(null); }}
        title="メルマガを配信しますか？"
        summary={`「${sendTarget?.subject}」を、購読中（confirmed）の読者全員へ配信します。配信後は編集できません。`}
        confirmText="配信する"
        onConfirm={() => sendTarget && send.mutate(sendTarget.id)}
        isPending={send.isPending}
      />

      <ConfirmDialog
        open={removeTarget != null}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title="下書きを削除しますか？"
        summary={`「${removeTarget?.subject}」を削除します。`}
        confirmText="削除する"
        irreversible
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        isPending={remove.isPending}
      />
    </>
  );
};

// ---------------------------------------------------------------- 購読者

type Subscriber = {
  id: string;
  email: string;
  status: 'pending' | 'confirmed' | 'unsubscribed';
  source: string;
  companyId: string | null;
  confirmedAt: string | null;
  createdAt: string;
};
type SubListResult = {
  data: Subscriber[];
  meta: { nextCursor: string | null; totalCount?: number };
  counts: { pending: number; confirmed: number; unsubscribed: number };
  signupUrl: string;
};
const STATUS_VARIANT: Record<Subscriber['status'], 'solid' | 'default' | 'muted'> = {
  confirmed: 'solid',
  pending: 'default',
  unsubscribed: 'muted',
};

const SubscribersTab = () => {
  const queryClient = useQueryClient();
  const { filters, setFilter } = useUrlFilters({ status: '', q: '', tab: 'subscribers' });
  const [addOpen, setAddOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [removeTarget, setRemoveTarget] = useState<Subscriber | null>(null);

  const query = { status: filters.status || undefined, q: filters.q || undefined, limit: 50 };
  const { data, isLoading } = useQuery({
    queryKey: ['newsletter', 'subscribers', query],
    queryFn: () => api.get<SubListResult>('/newsletter/subscribers', query),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['newsletter', 'subscribers'] });

  const add = useMutation({
    mutationFn: (v: string) => api.post('/newsletter/subscribers', { email: v }),
    onSuccess: () => { toast.success('購読者を追加しました'); invalidate(); setAddOpen(false); setEmail(''); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/newsletter/subscribers/${id}`),
    onSuccess: () => { toast.success('購読者を削除しました'); invalidate(); setRemoveTarget(null); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const columns: Column<Subscriber>[] = [
    { key: 'email', header: 'メールアドレス', render: (r) => <span className="break-all font-semibold">{r.email}</span> },
    { key: 'status', header: '状態', width: '110px', render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{NEWSLETTER_SUBSCRIBER_STATUS_LABELS[r.status]}</Badge> },
    { key: 'source', header: '経路', width: '90px', render: (r) => <span className="text-note">{r.source === 'signup' ? '登録フォーム' : r.source === 'manual' ? '手動' : r.source}</span> },
    { key: 'createdAt', header: '登録日時', width: '140px', render: (r) => <span className="tabular text-note">{formatDateTime(r.createdAt)}</span> },
  ];
  const counts = data?.counts;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {counts && (
          <Badge variant="muted">
            購読中 {counts.confirmed}・確認待ち {counts.pending}・解除 {counts.unsubscribed}
          </Badge>
        )}
        <Button onClick={() => { setAddOpen(true); setEmail(''); }}>
          <Plus aria-hidden />
          購読者を追加
        </Button>
      </div>

      {data?.signupUrl && (
        <div className="mb-3 rounded-md border border-border p-3">
          <p className="text-note font-medium">登録フォームの URL</p>
          <p className="mt-1 break-all text-note text-foreground-muted">{data.signupUrl}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => { void navigator.clipboard?.writeText(data.signupUrl); toast.success('URL をコピーしました'); }}>
              URL をコピー
            </Button>
          </div>
          <p className="mt-2 text-note text-foreground-subtle">
            この URL を「設定 → メール送信」のメルマガ登録 URL に設定すると、文面の {'{{newsletter_url}}'} に差し込まれます。
          </p>
        </div>
      )}

      <FilterChips className="mb-3">
        <Select value={filters.status as string} onChange={(e) => setFilter('status', e.target.value)} aria-label="状態で絞り込む" className="h-8 w-auto shrink-0 text-note lg:h-8">
          <option value="">状態：すべて</option>
          <option value="confirmed">購読中</option>
          <option value="pending">確認待ち</option>
          <option value="unsubscribed">解除済み</option>
        </Select>
        <Input type="search" value={filters.q as string} onChange={(e) => setFilter('q', e.target.value)} placeholder="メールで検索" aria-label="購読者を検索" className="h-8 w-56 shrink-0 text-note lg:h-8" />
      </FilterChips>

      <ResponsiveList
        columns={columns}
        rows={data?.data ?? []}
        isLoading={isLoading}
        rowActions={(row) => (
          <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(row)}>削除</Button>
        )}
        empty={<EmptyState title="購読者がいません" />}
        card={(row) => (
          <div className="space-y-1.5">
            <p className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 break-all text-body font-semibold">{row.email}</span>
              <Badge variant={STATUS_VARIANT[row.status]}>{NEWSLETTER_SUBSCRIBER_STATUS_LABELS[row.status]}</Badge>
            </p>
            <p className="tabular text-note text-foreground-subtle">{formatDateTime(row.createdAt)}</p>
            <Button variant="outline" size="sm" onClick={() => setRemoveTarget(row)}>削除</Button>
          </div>
        )}
      />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>購読者を追加</DialogTitle></DialogHeader>
          <DialogBody>
            <Field label="メールアドレス" hint="手動追加は確認待ちを飛ばして「購読中」になります">
              {(props) => (
                <Input {...props} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.co.jp" />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>キャンセル</Button>
            <Button onClick={() => email.trim() && add.mutate(email.trim())} disabled={add.isPending || !email.trim()}>追加</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget != null}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title="購読者を削除しますか？"
        summary={`「${removeTarget?.email}」を購読者から削除します。`}
        confirmText="削除する"
        irreversible
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        isPending={remove.isPending}
      />
    </>
  );
};
